<template>
  <div class="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
    <div class="flex items-center justify-between mb-3">
      <h4 class="text-sm font-medium text-gray-900 dark:text-white">
        {{ t('app.sysadmin.modal.admin_users') }}
      </h4>
      <button
        v-if="!hasAdmins"
        class="text-sm text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium"
        @click="showCreateAdminForm = true"
      >
        {{ t('app.sysadmin.modal.create_admin') }}
      </button>
    </div>
      
    <!-- Loading -->
    <div
      v-if="loadingAdmins"
      class="text-sm text-gray-500 dark:text-gray-400"
    >
      {{ t('app.common.loading') }}
    </div>
      
    <!-- No admins -->
    <div
      v-else-if="!hasAdmins && !showCreateAdminForm"
      class="text-sm text-amber-700 dark:text-amber-400"
    >
      ⚠️ {{ t('app.sysadmin.modal.no_admin_warning') }}
    </div>
      
    <!-- Admin list -->
    <div
      v-else-if="hasAdmins && !showCreateAdminForm"
      class="space-y-2"
    >
      <div
        v-for="admin in admins"
        :key="admin.id"
        class="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded-lg"
      >
        <div>
          <div class="text-sm font-medium text-gray-900 dark:text-white">
            {{ admin.full_name }}
          </div>
          <div class="text-xs text-gray-500 dark:text-gray-400">
            {{ admin.email }}
          </div>
        </div>
        <span class="text-xs px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full">
          Admin
        </span>
      </div>
      <button
        class="w-full text-sm text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium py-2"
        @click="showCreateAdminForm = true"
      >
        + {{ t('app.sysadmin.modal.add_another_admin') }}
      </button>
    </div>
      
    <!-- Create admin form -->
    <div
      v-if="showCreateAdminForm"
      class="space-y-3"
    >
      <div>
        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          {{ t('app.sysadmin.create_admin.full_name') }} *
        </label>
        <input
          v-model="adminForm.full_name"
          type="text"
          required
          class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
          :placeholder="t('app.sysadmin.create_admin.full_name_placeholder')"
        >
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          {{ t('app.sysadmin.create_admin.email') }} *
        </label>
        <input
          v-model="adminForm.email"
          type="email"
          required
          class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
          :placeholder="t('app.sysadmin.create_admin.email_placeholder')"
        >
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          {{ t('app.sysadmin.create_admin.password') }} *
        </label>
        <input
          v-model="adminForm.password"
          type="password"
          required
          minlength="8"
          class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
          :placeholder="t('app.sysadmin.create_admin.password_placeholder')"
        >
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {{ t('app.sysadmin.create_admin.password_requirements') }}
        </p>
      </div>
      <div class="flex gap-2">
        <button
          type="button"
          class="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          @click="resetAdminForm"
        >
          {{ t('app.common.cancel') }}
        </button>
        <button
          :disabled="creatingAdmin"
          class="flex-1 px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          @click="handleCreateAdmin"
        >
          {{ creatingAdmin ? t('app.common.loading') : t('app.sysadmin.create_admin.submit') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';

interface Admin {
  id: number;
  full_name: string;
  email: string;
}

interface AdminForm {
  full_name: string;
  email: string;
  password: string;
}

const { t } = useI18n();

defineProps<{
  loadingAdmins: boolean;
  admins: Admin[];
  showCreateAdminForm: boolean;
  creatingAdmin: boolean;
  adminForm: AdminForm;
  hasAdmins: boolean;
}>();

const emit = defineEmits<{
  (e: 'show-create-admin-form', show: boolean): void;
  (e: 'create-admin'): void;
  (e: 'cancel-create-admin'): void;
  (e: 'update:admin-form', form: AdminForm): void;
}>();

const handleCreateAdmin = () => {
  emit('create-admin');
};

const resetAdminForm = () => {
  emit('cancel-create-admin');
};
</script>
